import React from 'react';
import { connect } from 'react-redux';
import Translate from '../../../core/translate/Translate.js';
import SvgIcon from '../../../core/icons/SvgIcon.js';
import { Link, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { useModelInfo } from '../../common/ModelInfoContext.js';

export const AdChoicesFooterItem = ({ adChoicesUrl }) => {
  if (!adChoicesUrl) {
    return null;
  }

  const location = useLocation();

  return (
    <li className="footer__item">
      <Link
        to={`${location.pathname}/ads`}
        className="footer__link ad-choices-link stat-text-link"
        data-dtm="footer">
        <SvgIcon className="ad_choice" name="ad-choice" />

        <img
          className="footer__image"
          alt=""
          src="//c.betrad.com/pub/icon1.png"
        />
        <span>
          <Translate value="byo.footer.adchoice" />
        </span>
      </Link>
    </li>
  );
};

const LanguageToggle = (props) => {
  const {
    appResources,
    selections,
    serializedState,
    postalCode,
    pathname,
    contextPath,
  } = props;
  const {
    country,
    language,
    brand,
    carline,
    year,
    bodyStyle,
    languageToggleUrls,
  } = appResources;
  const selectedLanguage = language;
  const languageMaps = {
    en: 'component.language.toggle.english',
    fr: 'component.language.toggle.french',
  };
  const { rpos, accessories, styleId } = selections || {};
  const query = {
    styleId: styleId,
    rpo: rpos?.slice().concat(accessories).join(','),
    ss: serializedState,
    postalCode: postalCode,
  };

  const queryString = _.keys(query)
    .filter((key) => query[key])
    .map((key) => `${key}=${query[key]}`)
    .join('&');

  const languages = languageToggleUrls?.map(function (languageConfig) {
    const language = languageConfig.language;
    const contentPathNoSlash = _.trimStart(contextPath, '/');
    const baseUrl = [
      languageConfig.url,
      `${contentPathNoSlash}${contentPathNoSlash ? '/client' : 'client'}`,
      language,
      country,
      brand,
      carline,
      year,
      bodyStyle,
    ].join('/');
    const stepUrl = pathname.split('/').pop();
    const url = `${baseUrl}/${stepUrl}`;

    return {
      url: `${url}?${queryString}`,
      text: languageMaps[language],
      lang: language,
    };
  });

  if (languages?.length === 0) {
    return null;
  }

  const languageItems = languages?.map((language) => {
    const languageClassName = `footer__link stat-text-link ${
      language.lang === selectedLanguage ? 'language-toggle__active' : ''
    }`;
    return (
      <li className="footer__item" key={language.lang}>
        <a
          target="_self"
          rel="noreferrer"
          data-dtm="footer"
          className={languageClassName}
          href={language.url}>
          <Translate value={language.text} />
        </a>
      </li>
    );
  });

  return (
    <div className="language-toggle">
      <li className="footer__item">
        <span className="footer__text">
          <Translate value="footer.language.select.v3" />
        </span>
      </li>
      {languageItems}
    </div>
  );
};

export const FooterItem = ({ id, visible, url, label }) => {
  if (!visible) {
    return null;
  }

  return (
    <li className="footer__item">
      <a
        id={id}
        target="_blank"
        rel="noreferrer"
        className="footer__link stat-text-link"
        data-dtm="footer"
        href={url}>
        <Translate value={label} />
      </a>
    </li>
  );
};

const mapStateToProps = (state) => {
  const { config, params, reservation, appResources } = state;
  return {
    appResources,
    brand: appResources.brand,
    config,
    params,
    reservation,
  };
};

const Footer = ({
  appResources,
  className,
  brand,
  config,
  params,
  hideDealerInfo = false,
  reservation,
  analyticsKey = 'contact',
}) => {
  const { country, urls } = appResources;
  const {
    userGuidelinesUrl,
    adChoicesUrl,
    privacyPolicyUrl,
    californiaPrivacyUrl,
    copyrightTrademarkUrl,
  } = urls;
  const { contextPath, hasTransactionId, isEvMode } = useModelInfo();

  const analyticsQueryParam = `?evar25=${country.toLowerCase()}_t1_${brand}_footer`;

  return (
    <footer className={`row${className ? ` ${className}` : ''}`}>
      <div className="footer">
        {!hideDealerInfo && isEvMode && hasTransactionId && (
          <div className="footer__dealer-contact">
            <Translate
              value="ev.footer.contact.dealer"
              context={{ dealer: reservation.dealer.title }}
            />
            <a
              className="footer__dealer-contact--link stat-text-link"
              data-dtm={analyticsKey}
              href={`tel:${reservation.dealer.phone}`}>
              {reservation.dealer.phone}
            </a>
          </div>
        )}
        <ul className="footer__links">
          <FooterItem
            id="footer-copyright-link"
            visible={country === 'US'}
            url={copyrightTrademarkUrl + analyticsQueryParam}
            label="byo.footer.copyright"
          />
          {country === 'CA' && (
            <li className="footer__item">
              <span className="footer__text">
                <Translate value="byo.footer.gm" />
              </span>
            </li>
          )}
          <FooterItem
            id="footer-privacy-link"
            visible={privacyPolicyUrl}
            url={privacyPolicyUrl + analyticsQueryParam}
            label="footer.gm.privacy.v3"
          />
          <FooterItem
            id="footer-user-guidelines-link"
            visible={country != 'CA'}
            url={userGuidelinesUrl}
            label="footer.guidelines.v3"
          />
          {country !== 'CA' && (
            <AdChoicesFooterItem adChoicesUrl={adChoicesUrl} />
          )}
          <FooterItem
            id="footer-ca-legal-information-link"
            visible={californiaPrivacyUrl && country === 'US'}
            url={californiaPrivacyUrl}
            label="footer.ca.legal.information.v3"
          />
          {country === 'CA' && (
            <LanguageToggle
              appResources={appResources}
              selections={config?.selections}
              serializedState={config?.ss}
              postalCode={params?.postalCode}
              pathname={location.pathname}
              contextPath={contextPath}
            />
          )}
        </ul>
      </div>
    </footer>
  );
};

export default connect(mapStateToProps)(Footer);
